<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import { CustomCheckbox, Popover, PopoverContent, PopoverTrigger } from '@/shared/ui';
  import { useFormBuilderStore } from '../../model/form-builder.store';
  import { useFormRulesStore } from '../../model/form-rules.store';
  import { useCanvasHeaderOffset } from '../../composables/useCanvasHeaderOffset';
  import FormBuilderStageTitle from './FormBuilderStageTitle.vue';
  import FormCanvas from './FormCanvas.vue';
  import LockedRevisionNotice from './LockedRevisionNotice.vue';
  import RulesSelectionMenu from './RulesSelectionMenu.vue';

  interface Props {
    orientation: 'portrait' | 'landscape';
    zoom: number;
    showAnchorHighlights?: boolean;
    showReportHighlights?: boolean;
    locked?: boolean;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    'update:zoom': [value: number];
    'update:pageCount': [value: number];
    'update:currentPage': [value: number];
  }>();

  const formBuilderStore = useFormBuilderStore();
  const formRulesStore = useFormRulesStore();

  const isSelectAllChecked = computed(() => formRulesStore.isAllSelected);
  const isSelectAllDisabled = computed(() => {
    if (props.locked) return true;
    return formBuilderStore.elements.every((element) => element.type === 'HEADER');
  });

  const handleSelectAllChange = (event: Event) => {
    const target = event.target as HTMLInputElement;

    if (target.checked) {
      formRulesStore.selectAll([...formBuilderStore.elements]);
      return;
    }

    formRulesStore.clearSelection();
  };

  const handleDocumentKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (formRulesStore.menuAnchor.kind === 'none') return;

    formRulesStore.clearSelection();
  };

  const canvasContainerRef = ref<HTMLElement | null>(null);
  const { headerStyle } = useCanvasHeaderOffset(
    canvasContainerRef,
    computed(() => props.orientation),
    computed(() => props.zoom)
  );

  onMounted(() => {
    document.addEventListener('keydown', handleDocumentKeyDown);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleDocumentKeyDown);
  });
</script>

<template>
  <section class="flex min-w-0 flex-1 flex-col overflow-hidden bg-gray-12">
    <div class="flex flex-col gap-4 py-4">
      <div class="flex flex-col gap-4" :style="headerStyle">
        <FormBuilderStageTitle title="Configuração de Regras" />
        <LockedRevisionNotice v-if="props.locked" />

        <Popover
          :open="
            !formRulesStore.ruleItemSelectionActive &&
            formRulesStore.menuAnchor.kind === 'select-all'
          "
          @update:open="
            (open: boolean) => {
              if (!open) formRulesStore.clearSelection();
            }
          "
        >
          <PopoverTrigger as-child>
            <label class="flex w-fit items-center gap-3 text-sm leading-4 text-gray-07">
              <CustomCheckbox
                id="form-builder-rules-select-all"
                :checked="isSelectAllChecked"
                :disabled="isSelectAllDisabled"
                @change="handleSelectAllChange"
              />
              <span class="leading-none font-medium">Selecionar tudo</span>
            </label>
          </PopoverTrigger>
          <PopoverContent
            v-if="!formRulesStore.ruleItemSelectionActive"
            side="bottom"
            align="start"
            :side-offset="8"
            disable-portal
            disable-animations
            position-strategy="absolute"
            class="w-auto p-0"
          >
            <RulesSelectionMenu />
          </PopoverContent>
        </Popover>
      </div>
    </div>

    <div ref="canvasContainerRef" class="min-h-0 flex-1 overflow-hidden">
      <div
        class="flex h-full min-h-[480px] min-w-0 flex-col"
        data-testid="form-builder-rules-stage"
      >
        <FormCanvas
          :orientation="props.orientation"
          :zoom="props.zoom"
          :show-anchor-highlights="props.showAnchorHighlights"
          :show-report-highlights="props.showReportHighlights"
          interaction-mode="rules"
          :locked="props.locked"
          @update:zoom="emit('update:zoom', $event)"
          @update:pageCount="emit('update:pageCount', $event)"
          @update:currentPage="emit('update:currentPage', $event)"
        />
      </div>
    </div>
  </section>
</template>
